/**
 * Provides type-safe storage and retrieval with error handling
 */

import type { AuthUser } from "../../../shared/api";
import type { UserPreferences } from "../types/userPreferences";
import type { ProgressTracking } from "../types/database.types";

export interface UserSettings {
  emailNotifications: boolean;
  studyReminders: boolean;
  publicProfile: boolean;
  shareProgress: boolean;
  language: string;
  timezone: string;
  theme?: string;

  // Profile & Learning
  full_name?: string;
  role?: string;
  gradeLevel?: string;
  learningStyle?: string;
  studyGoal?: string;
  adaptiveDifficulty?: boolean;

  // AI & Study preferences
  aiPersona?: string;
  defaultQuizLength?: number;
  objectiveQuestionsCount?: number;
  subjectiveQuestionsCount?: number;

  // UI preferences
  hideTimer?: boolean;
  hideStreak?: boolean;
  minimalUI?: boolean;
  avatarSeed?: string;

  // Notifications & Privacy (matching Settings.tsx)
  achievementAlerts?: boolean;
  profilePublic?: boolean;
  showStats?: boolean;
}

export interface CachedProgress {
  data: Array<ProgressTracking>;
  timestamp: number;
  userId: string;
}

const STORAGE_KEYS = {
  USER_SETTINGS: "novari_user_settings",
  PROGRESS_CACHE: "novari_progress_cache",
  LAST_SYNC: "novari_last_sync",
  USER_PREFERENCES_CONTEXT: "novari_user_preferences",
  AUTH_USER: "novari_user",
  AVATAR_MIGRATED: "novari_avatar_migrated",
  USER_PREFERENCES: "user_preferences",
  WHATS_NEW: "novari_whats_new_v1",
  WELCOME_WALKTHROUGH: "novari_welcome_completed",
  LAST_GRADE_LEVEL: "novari_last_grade_level",
  ONBOARDING_DRAFT: "novari:onboarding-draft:v1",
  RECENT_SEARCHES: "novari:recent-searches",
  ACKNOWLEDGED_LEVEL: "novari_acknowledged_level",
} as const;

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Generic localStorage wrapper with error handling
 */
export class LocalStorageManager {
  static set<T>(key: string, value: T): boolean {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error(`Failed to save to localStorage (${key}):`, error);
      return false;
    }
  }

  static get<T>(key: string, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue ?? null;
      }
      return JSON.parse(item);
    } catch (error) {
      console.error(`Failed to read from localStorage (${key}):`, error);
      return defaultValue ?? null;
    }
  }

  static remove(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Failed to remove from localStorage (${key}):`, error);
      return false;
    }
  }

  static clear(): boolean {
    try {
      // Only clear our app's keys
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error("Failed to clear localStorage:", error);
      return false;
    }
  }
}

/**
 * Auth user persistence
 */
export const authStorage = {
  saveUser: (user: AuthUser): boolean => {
    return LocalStorageManager.set(STORAGE_KEYS.AUTH_USER, user);
  },

  getUser: (): AuthUser | null => {
    return LocalStorageManager.get<AuthUser>(STORAGE_KEYS.AUTH_USER);
  },

  clearUser: (): boolean => {
    return LocalStorageManager.remove(STORAGE_KEYS.AUTH_USER);
  },

  setAvatarMigrated: (): boolean => {
    return LocalStorageManager.set(STORAGE_KEYS.AVATAR_MIGRATED, true);
  },

  hasAvatarMigrated: (): boolean => {
    const val = LocalStorageManager.get<boolean>(STORAGE_KEYS.AVATAR_MIGRATED);
    if (val === true) return true;
    try {
      return localStorage.getItem(STORAGE_KEYS.AVATAR_MIGRATED) === "true";
    } catch {
      return false;
    }
  },
};

/**
 * User settings persistence (existing)
 */
export const userSettingsStorage = {
  save: (settings: UserSettings): boolean => {
    return LocalStorageManager.set(STORAGE_KEYS.USER_SETTINGS, {
      ...settings,
      lastUpdated: Date.now(),
    });
  },

  load: (): UserSettings | null => {
    const data = LocalStorageManager.get<
      Record<string, unknown> & { lastUpdated: number }
    >(STORAGE_KEYS.USER_SETTINGS);

    if (!data) return null;

    const { lastUpdated, ...settings } = data;
    return settings as unknown as UserSettings;
  },

  clear: (): boolean => {
    return LocalStorageManager.remove(STORAGE_KEYS.USER_SETTINGS);
  },
};

/**
 * User Preferences persistence (for UserPreferencesContext)
 * Uses "user_preferences" key to match existing app usage
 */
export const userPreferencesStorage = {
  save: (preferences: UserPreferences): boolean => {
    return LocalStorageManager.set(STORAGE_KEYS.USER_PREFERENCES, preferences);
  },

  load: (): UserPreferences | null => {
    return LocalStorageManager.get<UserPreferences>(STORAGE_KEYS.USER_PREFERENCES);
  },

  clear: (): boolean => {
    return LocalStorageManager.remove(STORAGE_KEYS.USER_PREFERENCES);
  },
};

/**
 * Progress data caching
 */
export const progressStorage = {
  save: (progress: Array<ProgressTracking>, userId: string): boolean => {
    const cacheData: CachedProgress = {
      data: progress,
      timestamp: Date.now(),
      userId,
    };
    return LocalStorageManager.set(STORAGE_KEYS.PROGRESS_CACHE, cacheData);
  },

  load: (userId: string): Array<ProgressTracking> | null => {
    const cache = LocalStorageManager.get<CachedProgress>(
      STORAGE_KEYS.PROGRESS_CACHE,
    );

    if (!cache || cache.userId !== userId) {
      return null;
    }

    // Check if cache is expired
    if (Date.now() - cache.timestamp > CACHE_DURATION) {
      LocalStorageManager.remove(STORAGE_KEYS.PROGRESS_CACHE);
      return null;
    }

    return cache.data;
  },

  clear: (): boolean => {
    return LocalStorageManager.remove(STORAGE_KEYS.PROGRESS_CACHE);
  },
};

/**
 * Sync timestamp tracking
 */
export const syncStorage = {
  saveLastSync: (timestamp: number = Date.now()): boolean => {
    return LocalStorageManager.set(STORAGE_KEYS.LAST_SYNC, { timestamp });
  },

  getLastSync: (): number | null => {
    const data = LocalStorageManager.get<{ timestamp: number }>(
      STORAGE_KEYS.LAST_SYNC,
    );
    return data?.timestamp ?? null;
  },

  clear: (): boolean => {
    return LocalStorageManager.remove(STORAGE_KEYS.LAST_SYNC);
  },
};

/**
 * Clear all user data (for logout or account deletion)
 */
// Account-derived values written outside STORAGE_KEYS. Device preferences
// (panel widths, view modes, nav layout) are deliberately not listed.
const PER_ACCOUNT_LOCAL_KEYS = [
  "novari_achievements",
  "novari_level",
  "novari_xp",
  "novari_streak",
  "novari_longest_streak",
  "novari_minutes",
  "novari_progressive_profile_v2",
  "novari:just-onboarded",
  "novari:streak-danger-nudged",
] as const;

/**
 * Called on sign-out and whenever the signed-in account changes. Besides the
 * localStorage keys above it drops every "novari:" sessionStorage entry: the
 * library, dashboard and analytics screens seed React Query's initialData from
 * those caches, so after queryClient.clear() the next account in the same tab
 * was shown the previous account's documents/stats until the refetch landed.
 */
export const clearAllUserData = (): boolean => {
  const cleared = LocalStorageManager.clear();
  try {
    PER_ACCOUNT_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("novari:")) sessionStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }
  return cleared;
};

/**
 * Check if localStorage is available
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const test = "__localStorage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * What's New modal tracking
 */
const WHATS_NEW_KEY = STORAGE_KEYS.WHATS_NEW;

export const hasSeenWhatsNew = (): boolean => {
  try {
    return localStorage.getItem(WHATS_NEW_KEY) === "true";
  } catch {
    return false;
  }
};

export const markWhatsNewAsSeen = (): void => {
  try {
    localStorage.setItem(WHATS_NEW_KEY, "true");
  } catch (error) {
    console.error("Failed to mark What's New as seen:", error);
  }
};

/**
 * Welcome Walkthrough tracking
 */
const WELCOME_WALKTHROUGH_KEY = STORAGE_KEYS.WELCOME_WALKTHROUGH;

export const hasCompletedWelcome = (): boolean => {
  try {
    return localStorage.getItem(WELCOME_WALKTHROUGH_KEY) === "true";
  } catch {
    return false;
  }
};

export const markWelcomeAsCompleted = (): void => {
  try {
    localStorage.setItem(WELCOME_WALKTHROUGH_KEY, "true");
  } catch (error) {
    console.error("Failed to mark welcome as completed:", error);
  }
};

/**
 * Last known grade level tracking
 */
export const gradeLevelStorage = {
  save: (gradeLevel: string): boolean => {
    return LocalStorageManager.set(STORAGE_KEYS.LAST_GRADE_LEVEL, gradeLevel);
  },
  load: (): string | null => {
    const saved = LocalStorageManager.get<string>(STORAGE_KEYS.LAST_GRADE_LEVEL);
    if (saved) return saved;
    try { return localStorage.getItem(STORAGE_KEYS.LAST_GRADE_LEVEL); } catch { return null; }
  },
  isKids: (): boolean => {
    // Must match KIDS_GRADE_LEVELS in src/types/userPreferences.ts
    const KIDS_GRADES = [
      "pre_k",
      "kindergarten",
      "lower_primary",
      "primary_1",
      "primary_2",
      "primary_3",
      "primary_4",
      "primary_5",
    ];
    // Try LocalStorageManager first (JSON-encoded), then raw string fallback
    // (KidsRouteGuard historically used raw localStorage.setItem)
    let gl = LocalStorageManager.get<string>(STORAGE_KEYS.LAST_GRADE_LEVEL);
    if (!gl) {
      try { gl = localStorage.getItem(STORAGE_KEYS.LAST_GRADE_LEVEL); } catch { /* Storage can be denied by the browser. */ }
    }
    return KIDS_GRADES.includes(gl ?? "");
  },
  clear: (): boolean => {
    return LocalStorageManager.remove(STORAGE_KEYS.LAST_GRADE_LEVEL);
  }
};

export default {
  userSettingsStorage,
  progressStorage,
  syncStorage,
  authStorage,
  clearAllUserData,
  isLocalStorageAvailable,
  hasSeenWhatsNew,
  markWhatsNewAsSeen,
  hasCompletedWelcome,
  markWelcomeAsCompleted,
  gradeLevelStorage,
};
